import type {
  BudgetSource,
  FamilyEnum,
  MarkdownState,
  PriceMarkdownErrorCode,
} from "@/@types/price-markdown";

export const FAMILY_OPTIONS: FamilyEnum[] = [
  "MASCULINO",
  "FEMININO",
  "INFANTIL",
  "CALCADOS",
  "ACESSORIOS_AV",
  "LAR",
];

export const FAMILY_END_OF_LIFE: FamilyEnum = "END_OF_LIFE";

export const FAMILY_OPTIONS_COLLECTION: FamilyEnum[] = [
  ...FAMILY_OPTIONS,
  FAMILY_END_OF_LIFE,
];

export const FAMILY_LABEL: Record<FamilyEnum, string> = {
  MASCULINO: "Masculino",
  FEMININO: "Feminino",
  INFANTIL: "Infantil",
  CALCADOS: "Calçados",
  ACESSORIOS_AV: "Acessórios Av",
  LAR: "Lar",
  END_OF_LIFE: "Mais de 120 dias",
};

export const STATE_LABEL: Record<MarkdownState, string> = {
  RASCUNHO: "Rascunho",
  SUBMETIDA: "Submetida",
  APROVADA: "Aprovada",
  EFETIVADA: "Efetivada",
  DESCARTADA: "Descartada",
  RECUSADA: "Recusada",
};

export const ALTERED_AT_PREFIX = "Alterada em";

export const MARKDOWN_STATE: { readonly [K in MarkdownState]: K } = {
  RASCUNHO: "RASCUNHO",
  SUBMETIDA: "SUBMETIDA",
  APROVADA: "APROVADA",
  EFETIVADA: "EFETIVADA",
  DESCARTADA: "DESCARTADA",
  RECUSADA: "RECUSADA",
};

export const SUBMITTABLE_STATES: ReadonlyArray<MarkdownState> = [
  MARKDOWN_STATE.RASCUNHO,
  MARKDOWN_STATE.RECUSADA,
];

export const EDITABLE_STATES: ReadonlyArray<MarkdownState> = [
  MARKDOWN_STATE.RASCUNHO,
  MARKDOWN_STATE.RECUSADA,
  MARKDOWN_STATE.SUBMETIDA,
];

export const RESUBMISSION_STATES: ReadonlyArray<MarkdownState> = [
  MARKDOWN_STATE.RECUSADA,
];

export const APPROVABLE_STATES: ReadonlyArray<MarkdownState> = [
  MARKDOWN_STATE.RASCUNHO,
  MARKDOWN_STATE.SUBMETIDA,
];

export const REFUSABLE_STATES: ReadonlyArray<MarkdownState> = [
  MARKDOWN_STATE.SUBMETIDA,
  MARKDOWN_STATE.APROVADA,
];

export const REFUSAL_COMMENT_MAX_LENGTH = 500;

export const ALL_FAMILIES_FILTER = "TODAS";
export const ALL_VALUES_FILTER = "TODOS";

export const withoutAllValuesFilter = (value: string): string | undefined => {
  if (value === ALL_VALUES_FILTER) {
    return undefined;
  }
  return value;
};

export const BULK_ACTION = {
  SUGERIR: "SUGERIR",
  REMARCAR: "REMARCAR",
  LIMPAR: "LIMPAR",
  LIMPAR_SUGESTAO: "LIMPAR_SUGESTAO",
} as const;

export const CYCLE_MODE = {
  ATUAL: "ATUAL",
  MES: "MES",
  PERIODO: "PERIODO",
} as const;

export type CycleMode = (typeof CYCLE_MODE)[keyof typeof CYCLE_MODE];

export const SELECTION_MODE = {
  LISTA: "LISTA",
  TODOS: "TODOS",
} as const;

export type SelectionMode = (typeof SELECTION_MODE)[keyof typeof SELECTION_MODE];

export const DISCOUNT_PCT_MIN = 0;
export const DISCOUNT_PCT_MAX = 99;
export const DISCOUNT_DEBOUNCE_MS = 500;
export const DISCOUNT_PREVIEW_DEBOUNCE_MS = 300;

export const BUDGET_TOLERANCE_RATIO = 0.2;

export const MATRIX_POLLING_INTERVAL_MS = 5000;

export const BUDGET_SOURCE = {
  DW: "DW",
  MANUAL: "MANUAL",
  RATEIO_DW: "RATEIO_DW",
  SEM_VERBA: "SEM_VERBA",
} as const;

export const BUDGET_SOURCE_LABEL: Record<BudgetSource, string> = {
  DW: "DW",
  MANUAL: "Digitado",
  RATEIO_DW: "Cota das famílias",
  SEM_VERBA: "Sem verba definida",
};

export const BUDGET_SOURCE_HELP: Record<BudgetSource, string> = {
  DW: "Valor do DW para este mês, já rateado: a família cede 1/(n+1) do próprio valor para o pool Mais de 120 dias, e o total do DW se preserva. Um valor digitado, se existir, é ignorado.",
  MANUAL:
    "O DW não trouxe a família neste mês; vale o valor digitado pelo administrador.",
  RATEIO_DW:
    "Cota das famílias: o pool Mais de 120 dias recebe a parte que cada família vinda do DW cede neste mês (total do DW dividido por n + 1).",
  SEM_VERBA:
    "O DW não trouxe a família neste mês e nenhum valor foi digitado. A verba do mês é zero até alguém digitar.",
};

export const BUDGET_SYNC_STATUS = {
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;

export const BUDGET_ENTRY_DESCRIPTION_MAX = 255;

export const TAG_DISREGARDED = "naoRemarca";
export const DISREGARDED_ITEM_LABEL = "Desconsiderado";
export const DISREGARDED_ITEM_TITLE =
  "Preço atual (PME) igual ou abaixo da menor ponta de preço: o item é desconsiderado da remarcação e da carta";
export const DISREGARDED_BADGE_LABEL = "Desconsiderados";
export const DISREGARDED_OUT_OF_LETTER =
  "não entram na carta: PME igual ou abaixo da menor ponta de preço";
export const DISREGARDED_LABEL =
  "desconsiderado(s): PME igual ou abaixo da menor ponta de preço";

export const SUGGESTED_BY_PREFIX = "Sugerido por ";

export const HTTP_STATUS_OK = 200;
export const HTTP_STATUS_ACCEPTED = 202;
export const HTTP_STATUS_BAD_REQUEST = 400;
export const HTTP_STATUS_FORBIDDEN = 403;
export const HTTP_STATUS_CONFLICT = 409;
export const HTTP_STATUS_TOO_MANY_REQUESTS = 429;

export const SAO_PAULO_TIME_ZONE = "America/Sao_Paulo";

export const AWAITING_APPLICATION_ERROR_CODE: PriceMarkdownErrorCode =
  "AWAITING_APPLICATION";
export const AWAITING_APPLICATION_LABEL = "aguardando aplicação";
export const AWAITING_APPLICATION_MARK_PREFIX = "Efetivado em";
export const AWAITING_APPLICATION_TITLE = "Itens aguardando aplicação";
export const AWAITING_APPLICATION_BLOCK_DESCRIPTION =
  "Itens efetivados cujo preço o DW ainda não aplicou. Desmarque-os ou aguarde a aplicação:";
export const BULK_IGNORED_LABEL =
  "ignorado(s): em outra remarcação ou aguardando aplicação";

export const ITEM_IN_OTHER_MARKDOWN_ERROR_CODE: PriceMarkdownErrorCode =
  "ITEM_IN_OTHER_MARKDOWN";
export const INVALID_TRANSITION_ERROR_CODE: PriceMarkdownErrorCode =
  "INVALID_TRANSITION";
export const ITEM_IN_OTHER_MARKDOWN_TITLE = "Itens em outra remarcação";
export const ITEM_IN_OTHER_MARKDOWN_BLOCK_DESCRIPTION =
  "Itens já remarcados em outra remarcação submetida ou aprovada e ainda não aplicados. Desmarque-os nesta remarcação:";
export const ITEM_IN_OTHER_MARKDOWN_GENERATE_DESCRIPTION =
  "Itens marcados em mais de uma remarcação submetida ou aprovada. Recuse uma delas ou desmarque os itens antes de gerar a carta:";
export const IN_MARKDOWN_MARK_PREFIX = "Em remarcação:";
export const IN_MARKDOWN_CREATED_AT = "criada em";
export const IN_MARKDOWN_TITLE =
  "Item já remarcado em outra remarcação e ainda não aplicado: aqui ele só pode ser desmarcado";
export const ITEM_STATUS_COLUMN_LABEL = "Situação";
export const ITEM_STATUS_AWAITING_LABEL = "Aguardando aplicação";
export const ITEM_STATUS_IN_MARKDOWN_LABEL = "Em remarcação";
export const ITEM_STATUS_ALL_LABEL = "Todas";
export const ITEM_STATUS_NONE_LABEL = "Sem situação";

export const LETTER_RELEASE_ITEMS_LABEL = "Liberar itens";
export const LETTER_RELEASE_TITLE = "Liberar itens da carta";
export const LETTER_RELEASE_DESCRIPTION =
  "Use só para carta que não vai subir no Logix (erro, desistência). Os itens voltam a poder ser remarcados e a liberação não se desfaz.";
export const LETTER_RELEASE_REASON_LABEL = "Motivo";
export const LETTER_RELEASE_REASON_MAX_LENGTH = 500;
export const LETTER_RELEASED_LABEL = "Liberada por";
export const LETTER_AWAITING_COLUMN = "Aguardando aplicação";

export const NEW_MARKDOWN_HEADER_PREFIX =
  "A nova remarcação será criada no ciclo";
export const NEW_MARKDOWN_OPEN_LATEST = "Abrir mais recente";
export const NEW_MARKDOWN_START_NEW = "Iniciar nova";
export const NEW_MARKDOWN_START_NEW_BLOCKED =
  "Já existe uma remarcação em edição — use Abrir mais recente";
